import dayjs from 'dayjs';

import type {
  DailyAttendanceStatus,
  MonthlyAttendanceDay,
  MonthlyAttendanceMark,
  MonthlyAttendanceStudent,
  MonthlyAttendanceView,
} from './api';

/**
 * How much of the register is on screen at once.
 *
 * `day` is the single-date column used by the "All batches" board — the
 * single-batch day view keeps its richer roster panel (notes, mark-all-present)
 * instead.
 */
export type RangeView = 'day' | 'week' | 'month';

export interface DateRange {
  /** ISO date, inclusive. */
  from: string;
  /** ISO date, inclusive. */
  to: string;
  /** Every date in `[from, to]`, ascending. */
  dates: string[];
  /** Human label for the range bar, e.g. "3 – 9 Aug 2026". */
  label: string;
}

/** Sunday-start weeks: the workday mask uses `extract(dow)` where 0 = Sunday. */
const WEEK_UNIT = 'week';

function enumerate(from: dayjs.Dayjs, to: dayjs.Dayjs): string[] {
  const out: string[] = [];
  for (let d = from; !d.isAfter(to, 'day'); d = d.add(1, 'day')) {
    out.push(d.format('YYYY-MM-DD'));
  }
  return out;
}

function rangeLabel(view: RangeView, from: dayjs.Dayjs, to: dayjs.Dayjs): string {
  if (view === 'day') return from.format('ddd, D MMM YYYY');
  if (view === 'month') return from.format('MMMM YYYY');
  // A week can straddle a month or a year boundary — only repeat the parts
  // that actually changed so the common case stays short.
  if (from.year() !== to.year()) {
    return `${from.format('D MMM YYYY')} – ${to.format('D MMM YYYY')}`;
  }
  if (from.month() !== to.month()) {
    return `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
  }
  return `${from.format('D')} – ${to.format('D MMM YYYY')}`;
}

/** The visible range for `view` containing `anchor` (an ISO date). */
export function rangeFor(view: RangeView, anchor: string): DateRange {
  const a = dayjs(anchor);
  const from = view === 'day' ? a : view === 'week' ? a.startOf(WEEK_UNIT) : a.startOf('month');
  const to = view === 'day' ? a : view === 'week' ? a.endOf(WEEK_UNIT) : a.endOf('month');
  return {
    from: from.format('YYYY-MM-DD'),
    to: to.format('YYYY-MM-DD'),
    dates: enumerate(from, to),
    label: rangeLabel(view, from, to),
  };
}

/**
 * Move the anchor one `view` unit and clamp so the board can never point past
 * today. Stepping forward from the current week lands on today rather than
 * refusing to move, which is what the "Today" button would have done anyway.
 */
export function stepAnchor(view: RangeView, anchor: string, delta: number, today: string): string {
  const unit = view === 'day' ? 'day' : view === 'week' ? WEEK_UNIT : 'month';
  const next = dayjs(anchor).add(delta, unit);
  const t = dayjs(today);
  return (next.isAfter(t, 'day') ? t : next).format('YYYY-MM-DD');
}

/** True when the range already includes today, i.e. there is nothing ahead. */
export function isAtPresent(view: RangeView, anchor: string, today: string): boolean {
  const unit = view === 'day' ? 'day' : view === 'week' ? WEEK_UNIT : 'month';
  return dayjs(anchor).isSame(dayjs(today), unit);
}

export interface MonthKey {
  year: number;
  /** 1..12 — matches `get_batch_monthly_view(p_month)`. */
  month: number;
}

/**
 * Which month payloads cover `[from, to]`.
 *
 * There is no range RPC: the register reads whole months through
 * `get_batch_monthly_view`, and a week that straddles a month boundary simply
 * needs both of them. Reusing the month cache also means the dashboard board
 * and the full register share query results instead of double-fetching.
 */
export function monthsInRange(from: string, to: string): MonthKey[] {
  const out: MonthKey[] = [];
  const last = dayjs(to).startOf('month');
  for (let m = dayjs(from).startOf('month'); !m.isAfter(last, 'month'); m = m.add(1, 'month')) {
    out.push({ year: m.year(), month: m.month() + 1 });
  }
  return out;
}

/**
 * Fold one or more month payloads into a single view.
 *
 * Later entries win on collision so a freshly refetched month replaces a stale
 * one. Students repeat across months and must be de-duplicated, otherwise a
 * cross-month week renders every student twice.
 */
export function mergeMonthViews(views: readonly MonthlyAttendanceView[]): MonthlyAttendanceView {
  const days = new Map<string, MonthlyAttendanceDay>();
  const students = new Map<string, MonthlyAttendanceStudent>();
  const marks = new Map<string, MonthlyAttendanceMark>();
  for (const v of views) {
    for (const d of v.days) days.set(d.date, d);
    for (const s of v.students) students.set(s.student_id, s);
    for (const m of v.marks) marks.set(`${m.student_id}|${m.date}`, m);
  }
  return {
    days: [...days.values()].sort((a, b) => a.date.localeCompare(b.date)),
    students: [...students.values()],
    marks: [...marks.values()],
  };
}

/** One cell's resolved mark — status plus whether a person ever confirmed it. */
export interface MarkInfo {
  status: DailyAttendanceStatus;
  /** true when the nightly auto-absent job wrote this row, not a person. */
  autoMarked: boolean;
}

/** `student_id → date → mark`, for O(1) cell lookup. */
export function indexMarks(
  marks: readonly MonthlyAttendanceMark[],
): Map<string, Map<string, MarkInfo>> {
  const out = new Map<string, Map<string, MarkInfo>>();
  for (const mark of marks) {
    let inner = out.get(mark.student_id);
    if (inner === undefined) {
      inner = new Map();
      out.set(mark.student_id, inner);
    }
    inner.set(mark.date, { status: mark.status, autoMarked: mark.auto_marked ?? false });
  }
  return out;
}

export interface DayMeta {
  is_workday: boolean;
  holiday_reason: string | null;
}

/** `date → workday/holiday metadata`. Dates the payload didn't cover are absent. */
export function indexDays(days: readonly MonthlyAttendanceDay[]): Map<string, DayMeta> {
  const out = new Map<string, DayMeta>();
  for (const d of days) {
    out.set(d.date, { is_workday: d.is_workday, holiday_reason: d.holiday_reason });
  }
  return out;
}

export interface AttendanceTotals {
  /** Present + late, counted only over the visible dates. */
  attended: number;
  /** Visible dates that are class days for this batch. */
  workdays: number;
  pct: number;
}

/**
 * Attendance for one student over exactly the visible dates, up to `cutoff`.
 *
 * Two windows, both load-bearing:
 *
 *  - The marks map holds whole months (see `monthsInRange`), so counting it
 *    wholesale against a 7-day workday count produces percentages well over
 *    100 in the week view.
 *  - A week or month in progress still has class days ahead of it. Counting
 *    those in the denominator reports a perfect-attendance student as 20% on
 *    Monday morning, which reads as a crisis rather than a partial week.
 */
export function totalsFor(
  dates: readonly string[],
  dayMeta: ReadonlyMap<string, DayMeta>,
  marks: ReadonlyMap<string, MarkInfo> | undefined,
  cutoff: string,
): AttendanceTotals {
  let attended = 0;
  let workdays = 0;
  for (const date of dates) {
    if (date > cutoff) continue;
    if (dayMeta.get(date)?.is_workday !== true) continue;
    workdays += 1;
    const status = marks?.get(date)?.status;
    if (status === 'present' || status === 'late') attended += 1;
  }
  return {
    attended,
    workdays,
    pct: workdays === 0 ? 0 : Math.round((attended / workdays) * 100),
  };
}

/** Initials for the roster avatar; falls back to the local part of the email. */
export function initialsFor(name: string, email: string): string {
  const source = name === '' ? (email.split('@')[0] ?? '') : name;
  const parts = source.replace(/[._-]/g, ' ').split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
